/**
 * This provides a local translation layer for all items int he design-system.
 *
 * - NOTE: These are the base and are exposed for overwriting within the calling
 * parent components.
 * - NOTE 2: The translations are kept here to provide for `storybook` internationalization.
 */
export const translations = {
  formFields: {
    rememberMe: {
      en: 'Remember Me',
      es: 'Recuérdame',
      ar: 'تذكر حسابي',
      he: 'זכור אותי',
      fr: 'Se souvenir de moi',
      ro: 'Ține-mă minte',
      nl: 'Onthoud mij',
      pl: 'Zapamiętaj mnie',
    },
    rememberMeTitle: {
      en: "If selected, you won't have to login in again next time even when you close your browser.",
      es: 'Si lo seleccionas, no tendrás que iniciar sesión de nuevo la próxima vez que accedas (después de cerrar el navegador).',
      ar: 'إذا تم تحديده ، فلن تضطر إلى تسجيل الدخول مرة أخرى في المرة القادمة حتى عندما تغلق المتصفح.',
      he: 'אם נבחר, לא תצתרך להתחבר שוב בפעם הבאה אפילו אם תסגור את הדפדפן',
      fr: "Si sélectionné, vous n'aurez pas à vous reconnecter la prochaine fois, même si vous fermez votre navigateur.",
      ro: 'Dacă este selectat, nu va trebui să vă conectați din nou data viitoare, chiar dacă închideți browserul.',
      nl: 'Als geselecteerd, hoef je de volgende keer niet opnieuw in te loggen, zelfs niet als je je browser sluit.',
      pl: 'Jeśli wybrane, nie będziesz musiał logować się ponownie następnym razem, nawet po zamknięciu przeglądarki.',
    },
  },
  formSubmission: {
    pleaseWait: {
      en: 'Please Wait',
      es: 'Espera un momento',
      ar: 'ارجوك انتظر',
      he: 'אנא המתן',
      fr: 'Veuillez patienter',
      ro: 'Vă rugăm să așteptați',
      nl: 'Even geduld',
      pl: 'Proszę czekać',
    },
    verifyingDetails: {
      en: 'Verifying your details.',
      es: 'Verificando tus datos.',
      ar: ' التحقق من التفاصيل الخاصة بك.',
      he: 'מאמת את הנתונים',
      fr: 'Vérification de vos informations.',
      ro: 'Verificarea detaliilor dvs.',
      nl: 'Je gegevens worden geverifieerd.',
      pl: 'Weryfikowanie Twoich danych.',
    },
  },
  documentUpload: {
    uploadCTA: {
      en: 'Click to Upload',
      es: 'Pulsa para cargar',
      ar: 'انقر للتحميل',
      he: 'לחץ להעלאה',
      fr: 'Cliquez pour télécharger',
      ro: 'Faceți clic pentru a încărca',
      nl: 'Klik om te uploaden',
      pl: 'Kliknij, aby przesłać',
    },
    addAnother: {
      en: 'Add another',
      es: 'Añadir otro',
      ar: 'أضف آخر',
      he: 'הוסף קובץ נוסף',
      fr: 'Ajouter un autre',
      ro: 'Adaugă altul',
      nl: 'Nog een toevoegen',
      pl: 'Dodaj kolejny',
    },
    uploadDone: {
      en: 'Upload successful',
      es: 'Carga completada',
      ar: 'تحميل ناجح',
      he: 'העלאה הושלמה',
      fr: 'Téléchargement réussi',
      ro: 'Încărcare reușită',
      nl: 'Upload geslaagd',
      pl: 'Przesyłanie zakończone sukcesem',
    },
    modalCTA: {
      en: 'Drop the Files here',
      es: 'Arrastra aquí los archivos',
      ar: 'أسقط الملفات هنا',
      he: 'העלה את הקבצים כאן',
      fr: 'Déposez les fichiers ici',
      ro: 'Lăsați fișierele aici',
      nl: 'Sleep de bestanden hierheen',
      pl: 'Upuść pliki tutaj',
    },
    modalDesc: {
      en: 'Drag and drop the files here or Click to select a file',
      es: 'Arrastra los archivos aquí o pulsa para seleccionar un archivo.',
      ar: 'اسحب الملفات وأفلتها هنا أو انقر لتحديد ملف',
      he: 'גרור והעלה את הקבצים כאן או לחץ כדי לבחור קובץ',
      fr: 'Glissez et déposez les fichiers ici ou cliquez pour sélectionner un fichier',
      ro: 'Trageți și plasați fișierele aici sau faceți clic pentru a selecta un fișier',
      nl: 'Sleep de bestanden hierheen of klik om een bestand te selecteren',
      pl: 'Przeciągnij i upuść pliki tutaj lub kliknij, aby wybrać plik',
    },
    modalAcceptedFileType: {
      en: 'Supports .jpeg, .jpg, .png with max size of 15 MB',
      es: 'Admite .jpeg, .jpg, .png con un tamaño máximo de 15 MB.',
      ar: 'يدعم .jpeg ، .jpg ، .png بحد أقصى 15 ميجا بايت',
      he: 'תומך בקבצי JPEG, JPG, PNG עד גודל קובץ 15 MB',
      fr: 'Prend en charge .jpeg, .jpg, .png avec une taille maximale de 15 Mo',
      ro: 'Acceptă .jpeg, .jpg, .png cu o dimensiune maximă de 15 MB',
      nl: 'Ondersteunt .jpeg, .jpg, .png met een maximale grootte van 15 MB',
      pl: 'Obsługuje .jpeg, .jpg, .png o maksymalnym rozmiarze 15 MB',
    },
    maxUploadSizeError: {
      en: 'File size is too large. Files cannot be larger than 15 MB',
      es: 'El archivo es demasiado grande. Los archivos no pueden tener más de 15 MB.',
      ar: 'حجم الملف كبير جدًا. لا يمكن أن يزيد حجم الملفات عن 15 ميغا بايت',
      he: 'גודל הקובץ רחב מידי. גודל קובץ אינו יכול לעלות על 15 MB',
      fr: 'La taille du fichier est trop grande. Les fichiers ne peuvent pas dépasser 15 Mo',
      ro: 'Dimensiunea fișierului este prea mare. Fișierele nu pot fi mai mari de 15 MB',
      nl: 'Bestandsgrootte is te groot. Bestanden mogen niet groter zijn dan 15 MB',
      pl: 'Rozmiar pliku jest zbyt duży. Pliki nie mogą być większe niż 15 MB',
    },
    uploadFailed: {
      en: 'Upload Failed',
      es: 'La carga ha fallado',
      ar: 'التحميل فشل',
      he: ' העלאה נכשלה',
      fr: 'Échec du téléchargement',
      ro: 'Încărcarea a eșuat',
      nl: 'Upload mislukt',
      pl: 'Przesyłanie nie powiodło się',
    },
    uploadButton: {
      en: 'Upload',
      es: 'Cargar',
      ar: 'رفع',
      he: 'העלאה',
      fr: 'Télécharger',
      ro: 'Încărcare',
      nl: 'Uploaden',
      pl: 'Prześlij',
    },
    closeButton: {
      en: 'Close',
      es: 'Cerrar',
      ar: 'يغلق',
      he: 'סגור',
      fr: 'Fermer',
      ro: 'Închide',
      nl: 'Sluiten',
      pl: 'Zamknij',
    },
  },
  camera: {
    capture: {
      en: 'Capture',
      es: 'Hacer foto',
      ar: 'يأسر',
      he: 'צלם',
      fr: 'Capturer',
      ro: 'Captură',
      nl: 'Vastleggen',
      pl: 'Zrób zdjęcie',
    },
    capturing: {
      en: 'Processing',
      es: 'Procesando',
      ar: 'جاري المعالجة',
      he: 'מעבד',
      fr: 'Traitement en cours',
      ro: 'Se procesează',
      nl: 'Verwerken',
      pl: 'Przetwarzanie',
    },
    refreshingCamera: {
      en: 'Refreshing the Camera',
      es: 'Reiniciando la cámara',
      ar: 'تحديث الكاميرا',
      he: 'מרענן מצלמה',
      fr: 'Rafraîchissement de la caméra',
      ro: 'Reîmprospătare cameră',
      nl: 'Camera verversen',
      pl: 'Odświeżanie kamery',
    },
    refreshPleaseWait: {
      en: 'please wait',
      es: 'espera un momento',
      ar: 'أرجو الإنتظار',
      he: 'אנא המתן',
      fr: 'veuillez patienter',
      ro: 'vă rugăm să așteptați',
      nl: 'even geduld',
      pl: 'proszę czekać',
    },
    cameraAccessErrorMessage: {
      en: 'We failed to access the camera. Please allow the camera access permission and try again.',
      es: 'No se puede acceder a la cámara. Permite el acceso a la cámara e inténtalo de nuevo.',
      ar: 'فشلنا في الوصول إلى الكاميرا. يرجى السماح بإذن الوصول إلى الكاميرا والمحاولة مرة أخرى.',
      he: 'לא הצלחנו לגשת למצלמה. אנא אפשר הרשאת גישה למצלמה ונסה שוב.',
      fr: "Nous n'avons pas réussi à accéder à la caméra. Veuillez autoriser l'accès à la caméra et réessayer.",
      ro: 'Nu am reușit să accesăm camera. Vă rugăm să permiteți accesul la cameră și să încercați din nou.',
      nl: 'Het is niet gelukt om toegang tot de camera te krijgen. Sta camera-toegang toe en probeer het opnieuw.',
      pl: 'Nie udało się uzyskać dostępu do kamery. Proszę zezwolić na dostęp do kamery i spróbować ponownie.',
    },
  },
  cropping: {
    back: {
      en: 'Back',
      es: 'Volver',
      ar: 'خبز',
      he: 'חזרה',
      fr: 'Retour',
      ro: 'Înapoi',
      nl: 'Terug',
      pl: 'Wstecz',
    },
    cancel: {
      en: 'Cancel',
      es: 'Cancelar',
      ar: 'يلغي',
      he: 'בטל',
      fr: 'Annuler',
      ro: 'Anulează',
      nl: 'Annuleren',
      pl: 'Anuluj',
    },
    preview: {
      en: 'Preview',
      es: 'Vista previa',
      ar: 'معاينة',
      he: 'תצוגה מקדימה',
      fr: 'Aperçu',
      ro: 'Previzualizare',
      nl: 'Voorbeeld',
      pl: 'Podgląd',
    },
    upload: {
      en: 'Upload',
      es: 'Cargar',
      ar: 'رفع',
      he: 'העלה קובץ',
      fr: 'Téléverser',
      ro: 'Încărcare',
      nl: 'Uploaden',
      pl: 'Prześlij',
    },
  },
  footer: {
    copyright: {
      en: 'Copyright © ',
      es: 'Copyright © ',
      ar: 'حقوق النشر © ',
      he: 'זכויות יוצרים © ',
      fr: "Droit d'auteur © ",
      ro: 'Drepturi de autor © ',
      nl: 'Auteursrecht © ',
      pl: 'Prawa autorskie © ',
    },
    rightsReserved: {
      en: 'All rights reserved.',
      es: 'Todos los derechos reservados.',
      ar: 'كل الحقوق محفوظة.',
      he: 'כל הזכויות שמורות',
      fr: 'Tous droits réservés.',
      ro: 'Toate drepturile rezervate.',
      nl: 'Alle rechten voorbehouden.',
      pl: 'Wszelkie prawa zastrzeżone.',
    },
  },
  forms: {
    validateOtp: {
      en: 'Validate OTP',
      es: 'Validar contraseña de un solo uso',
      ar: 'التحقق من صحة كلمة المرور لمرة واحدة',
      he: 'אמת קוד חד פעמי',
      fr: 'Valider le OTP',
      ro: 'Validează OTP',
      nl: 'OTP valideren',
      pl: 'Zweryfikuj OTP',
    },
    pleaseWait: {
      en: 'Please Wait',
      es: 'Espera un momento',
      ar: 'ارجوك انتظر',
      he: 'אנא המתן',
      fr: 'Veuillez patienter',
      ro: 'Vă rugăm să așteptați',
      nl: 'Even geduld',
      pl: 'Proszę czekać',
    },
    verify: {
      en: 'Verify Code',
      es: 'Verificar código',
      ar: 'التحقق من كود',
      he: 'אמת קוד',
      fr: 'Vérifier le code',
      ro: 'Verifică codul',
      nl: 'Code verifiëren',
      pl: 'Zweryfikuj kod',
    },
    verifyNumber: {
      en: 'Verify Number',
      es: 'Verificar número',
      ar: 'تحقق من الرقم',
      he: 'אמת מספר',
      fr: 'Vérifier le numéro',
      ro: 'Verifică numărul',
      nl: 'Nummer verifiëren',
      pl: 'Zweryfikuj numer',
    },
    sendingOtp: {
      en: 'Sending OTP, Please wait',
      es: 'Enviando contraseña de un solo uso. Espera un momento.',
      ar: 'إرسال OTP ، من فضلك انتظر',
      he: 'שולח קוד חד פעמי, אנא המתן',
      fr: 'Envoi du OTP, veuillez patienter',
      ro: 'Se trimite OTP, vă rugăm să așteptați',
      nl: 'OTP verzenden, even geduld',
      pl: 'Wysyłanie OTP, proszę czekać',
    },
  },
  otpModal: {
    inputCapture: {
      title: {
        en: 'OTP Verification',
        es: 'Verificación de la contraseña de un solo uso',
        ar: 'التحقق من OTP',
        he: 'אימות קוד חד פעמי',
        fr: 'Vérification OTP',
        ro: 'Verificare OTP',
        nl: 'OTP-verificatie',
        pl: 'Weryfikacja OTP',
      },
      message: {
        en: 'Enter the OTP sent to your mobile number',
        es: 'Introduce la contraseña de un solo uso que se ha enviado a tu móvil.',
        ar: 'أدخل OTP المرسل إلى رقم هاتفك المحمول',
        he: 'הכנס את הקוד אשר נשלח למכשיר הנייד שלך',
        fr: 'Entrez le OTP envoyé à votre numéro de téléphone',
        ro: 'Introduceți OTP-ul trimis pe numărul dvs. de telefon',
        nl: 'Voer de OTP in die naar uw mobiele nummer is verzonden',
        pl: 'Wprowadź OTP wysłane na Twój numer telefonu',
      },
      description: (phoneNumber: string) => ({
        en: `The OTP was sent to ${phoneNumber}`,
        es: `El OTP fue enviado a ${phoneNumber}`,
        ar: `${phoneNumber} تم إرسال OTP إلى`,
        he: `הקוד נשלח למס ${phoneNumber}`,
        fr: `Le OTP a été envoyé au ${phoneNumber}`,
        ro: `OTP-ul a fost trimis la ${phoneNumber}`,
        nl: `De OTP is verzonden naar ${phoneNumber}`,
        pl: `OTP zostało wysłane na ${phoneNumber}`,
      }),
      resendCodeMessage: {
        en: 'Did not receive the Code?',
        es: '¿No has recibido el código?',
        ar: 'لم تحصل على المدونة؟',
        he: 'לא קיבלת את הקוד?',
        fr: "Vous n'avez pas reçu le code ?",
        ro: 'Nu ați primit codul?',
        nl: 'Code niet ontvangen?',
        pl: 'Nie otrzymałeś kodu?',
      },
      resendCode: {
        en: 'Resend Code',
        es: 'Volver a enviar el código',
        ar: 'أعد إرسال الرمز',
        he: 'שלח קוד מחדש',
        fr: 'Renvoyer le code',
        ro: 'Retrimite codul',
        nl: 'Code opnieuw verzenden',
        pl: 'Wyślij kod ponownie',
      },
    },
    loadingState: {
      title: {
        en: 'Sending OTP',
        es: 'Enviando contraseña de un solo uso',
        ar: 'إرسال OTP',
        he: 'שולח קוד חד פעמי',
        fr: 'Envoi du OTP',
        ro: 'Se trimite OTP',
        nl: 'OTP verzenden',
        pl: 'Wysyłanie OTP',
      },
      message: (phoneNumber: string) => ({
        en: `We are sending a one time password to your mobile ${phoneNumber}`,
        es: `Te enviaremos una contraseña de un solo uso a tu móvil ${phoneNumber}`,
        ar: `نرسل كلמת مرور لمرة واحدة إلى هاتفك المحمول ${phoneNumber}`,
        he: ` אנו שולחים קוד חד פעמי למכשירך ${phoneNumber}`,
        fr: `Nous envoyons un mot de passe à usage unique à votre mobile ${phoneNumber}`,
        ro: `Trimitem o parolă unică pe telefonul dvs. ${phoneNumber}`,
        nl: `We sturen een eenmalig wachtwoord naar uw mobiele ${phoneNumber}`,
        pl: `Wysyłamy jednorazowe hasło na Twój telefon ${phoneNumber}`,
      }),
      info: {
        en: 'Please check your text messages for the OTP code.',
        es: 'Busca en tus mensajes la contraseña de un solo uso.',
        ar: 'يرجى التحقق من الرسائل النصية الخاصة بك للحصول على رمز OTP.',
        he: 'אנא בדוק את היסטורית ההודעות שלך עבור הקוד החד פעמי',
        fr: 'Veuillez vérifier vos messages texte pour le code OTP.',
        ro: 'Vă rugăm să verificați mesajele text pentru codul OTP.',
        nl: 'Controleer uw sms-berichten voor de OTP-code.',
        pl: 'Proszę sprawdzić wiadomości tekstowe pod kątem kodu OTP.',
      },
    },
    errorState: {
      title: {
        en: 'OTP Error',
        es: 'Contraseña de un solo uso errónea',
        ar: 'خطأ في OTP',
        he: ' תקלה בקוד החד פעמי',
        fr: 'Erreur OTP',
        ro: 'Eroare OTP',
        nl: 'OTP-fout',
        pl: 'Błąd OTP',
      },
      message: {
        en: 'Sorry, something went wrong',
        es: 'Algo ha salido mal',
        ar: 'عذرا، هناك خطأ ما',
        he: 'אנו מתנצלים, משהו השתבש',
        fr: 'Désolé, un problème est survenu',
        ro: 'Ne pare rău, ceva nu a mers bine',
        nl: 'Sorry, er is iets misgegaan',
        pl: 'Przepraszamy, coś poszło nie tak',
      },
      description: {
        en: 'OTP not sent.',
        es: 'Contraseña de un solo uso no enviada.',
        ar: 'لم يتم إرسال OTP.',
        he: 'קוד חד פעמי לא נשלח',
        fr: 'OTP non envoyé.',
        ro: 'OTP-ul nu a fost trimis.',
        nl: 'OTP niet verzonden.',
        pl: 'OTP nie zostało wysłane.',
      },
      description2: {
        en: 'The OTP could not be sent to the mobile number',
        es: 'No se ha podido enviar al móvil la contraseña de un solo uso.',
        ar: 'تعذر إرسال كلمة المرور لمرة واحدة إلى رقم الهاتف المحمول',
        he: 'הקוד לא יכל להשלח למספר הזה',
        fr: "Le OTP n'a pas pu être envoyé au numéro de téléphone",
        ro: 'OTP-ul nu a putut fi trimis la numărul de telefon',
        nl: 'De OTP kon niet naar het mobiele nummer worden verzonden',
        pl: 'Nie można było wysłać OTP na numer telefonu',
      },
      pleaseRetry: {
        en: 'Please Retry',
        es: 'Inténtalo de nuevo',
        ar: 'الرجاء اعادة المحاولة',
        he: 'אנא נסה שוב',
        fr: 'Veuillez réessayer',
        ro: 'Vă rugăm să încercați din nou',
        nl: 'Probeer opnieuw',
        pl: 'Proszę spróbować ponownie',
      },
      resendingOtp: {
        en: 'Resending OTP',
        es: 'Reenviando contraseña de un solo uso',
        ar: 'إعادة إرسال OTP',
        he: 'שולח קוד חד פעמי מחדש',
        fr: 'Renvoi du OTP',
        ro: 'Se retrimite OTP',
        nl: 'OTP opnieuw verzenden',
        pl: 'Ponowne wysyłanie OTP',
      },
    },
  },
  /**
   * -------------------------------------------------------------------------
   * GPS Location TRANSLATIONS
   */
  gpsField: {
    gpsLocation: {
      en: 'Store Location',
      es: 'Ubicación de la tienda',
      ar: 'موقع المتجر',
      he: 'מיקום חנות',
      fr: 'Emplacement du magasin',
      ro: 'Locația magazinului',
      nl: 'Winkel locatie',
      pl: 'Lokalizacja sklepu',
    },
    gpsPlaceholder: {
      en: 'Please select a store location(GPS)',
      es: 'Selecciona la ubicación de la tienda (GPS)',
      ar: 'نظام تحديد المواقع',
      he: 'אנא בחר חנות (GPS)',
      fr: 'Veuillez sélectionner un emplacement de magasin (GPS)',
      ro: 'Vă rugăm să selectați o locație de magazin (GPS)',
      nl: 'Selecteer een winkellocatie (GPS)',
      pl: 'Proszę wybrać lokalizację sklepu (GPS)',
    },
    theGPS: {
      en: 'The location (GPS)',
      es: 'Ubicación (GPS)',
      ar: 'الموقع (GPS)',
      he: 'המיקום (GPS)',
      fr: "L'emplacement (GPS)",
      ro: 'Locația (GPS)',
      nl: 'De locatie (GPS)',
      pl: 'Lokalizacja (GPS)',
    },
    confirmLocation: {
      en: 'Confirm',
      es: 'Confirmar',
      ar: 'يتأكد',
      he: 'אשר',
      fr: 'Confirmer',
      ro: 'Confirmă',
      nl: 'Bevestigen',
      pl: 'Potwierdź',
    },
    cancelLocation: {
      en: 'Cancel',
      es: 'Cancelar',
      ar: 'يلغي',
      he: 'בטל',
      fr: 'Annuler',
      ro: 'Anulează',
      nl: 'Annuleren',
      pl: 'Anuluj',
    },
    latitude: {
      en: 'Latitude',
      es: 'Latitud',
      ar: 'خط العرض',
      he: 'קו רוחב',
      fr: 'Latitude',
      ro: 'Latitudine',
      nl: 'Breedtegraad',
      pl: 'Szerokość geograficzna',
    },
    longitude: {
      en: 'Longitude',
      es: 'Longitud',
      ar: 'خط الطول',
      he: 'קו אורך',
      fr: 'Longitude',
      ro: 'Longitudine',
      nl: 'Lengtegraad',
      pl: 'Długość geograficzna',
    },
    locateMeButton: {
      en: 'Locate Me',
      es: 'Localízame',
      ar: 'تحديد موقع لي',
      he: 'מצא אותי',
      fr: 'Localisez-moi',
      ro: 'Localizează-mă',
      nl: 'Vind mij',
      pl: 'Znajdź mnie',
    },
  },
  tradeInDeviceDropdown: {
    title: {
      en: 'Select Device Categories',
      es: 'Seleccionar categoría del dispositivo',
      ar: 'حدد فئات الأجهزة',
      he: 'בחר קטגוריית מוצר',
      fr: "Sélectionner les catégories d'appareils",
      ro: 'Selectați categoriile de dispozitive',
      nl: 'Selecteer apparaatscategorieën',
      pl: 'Wybierz kategorie urządzeń',
    },
    subtitle: {
      en: 'Devices',
      es: 'Dispositivos',
      ar: 'الأجهزة',
      he: 'מכשירים',
      fr: 'Appareils',
      ro: 'Dispozitive',
      nl: 'Apparaten',
      pl: 'Urządzenia',
    },
  },
};
